import type { Command } from "../models/commands.ts";
import type { GamePhase } from "../models/phase.ts";
import type { WorldState } from "../models/worldState.ts";
import { reduceChooseStart } from "./commands/chooseStart.ts";
import { reduceCompleteSettle } from "./commands/completeSettle.ts";
import { reduceEndTurn } from "./commands/endTurn.ts";
import { reduceMove } from "./commands/move.ts";
import {
  reduceDismissNotification,
  reducePushNotification,
  reduceResolveNotificationDecision,
  reduceSelectNotificationNext,
  reduceSelectNotificationPrevious,
} from "./commands/notifications.ts";
import { reduceSelectTotem } from "./commands/selectTotem.ts";
import { resolveWorldBalance } from "../models/config/worldBalance.ts";
import type { ReduceContext } from "./context.ts";
import { applyAutoNotifications } from "./notifications/applyAutoNotifications.ts";
import { rejectCommand } from "./rejectCommand.ts";
import type { ReduceResult } from "./result.ts";

const COMMAND_PHASES: Record<Command["type"], GamePhase> = {
  SelectTotem: "selectingTotem",
  ChooseStart: "selectingStart",
  CompleteSettle: "settling",
  Move: "playing",
  EndTurn: "playing",
  PushNotification: "playing",
  DismissNotification: "playing",
  ResolveNotificationDecision: "playing",
  SelectNotificationPrevious: "playing",
  SelectNotificationNext: "playing",
};

const NOTIFICATION_COMMANDS = new Set<Command["type"]>([
  "PushNotification",
  "DismissNotification",
  "ResolveNotificationDecision",
  "SelectNotificationPrevious",
  "SelectNotificationNext",
]);

const NOTIFICATION_PHASES = new Set<GamePhase>([
  "selectingTotem",
  "selectingStart",
  "settling",
  "playing",
]);

function isKnownCommand(command: Command): boolean {
  return Object.prototype.hasOwnProperty.call(COMMAND_PHASES, command.type);
}

function isCommandAllowedInPhase(command: Command, phase: GamePhase): boolean {
  if (NOTIFICATION_COMMANDS.has(command.type)) {
    return NOTIFICATION_PHASES.has(phase);
  }
  return phase === COMMAND_PHASES[command.type];
}

export function reduce(
  state: WorldState,
  command: Command,
  context: ReduceContext = {},
): ReduceResult {
  if (!isKnownCommand(command)) {
    return rejectCommand(state, command, "unknown-command");
  }

  if (!isCommandAllowedInPhase(command, state.phase)) {
    return rejectCommand(state, command, "wrong-phase");
  }

  let result: ReduceResult;
  switch (command.type) {
    case "SelectTotem":
      result = reduceSelectTotem(state, command.name, command.totemId, context);
      break;
    case "ChooseStart":
      result = reduceChooseStart(state, command.tile);
      break;
    case "CompleteSettle":
      result = reduceCompleteSettle(state);
      break;
    case "Move":
      result = reduceMove(state, command.tile);
      break;
    case "EndTurn":
      result = reduceEndTurn(state, context);
      break;
    case "PushNotification":
      return reducePushNotification(state, command);
    case "DismissNotification":
      return reduceDismissNotification(state, command.id);
    case "ResolveNotificationDecision":
      return reduceResolveNotificationDecision(state, command.id, command.choice);
    case "SelectNotificationPrevious":
      return reduceSelectNotificationPrevious(state);
    case "SelectNotificationNext":
      return reduceSelectNotificationNext(state);
  }

  if (context.autoNotifications === false) {
    return result;
  }
  return applyAutoNotifications(result, resolveWorldBalance(context.balance));
}
